import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, cn, Dialog, Icon, toast } from "@sagot/ui";
import { useState, type ReactNode } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { datedFilename, saveBlob } from "../shared/download";
import { ConversationAttributesBlock } from "../shared/attribute-values/attribute-values-editor";
import { useFormatters } from "../shared/format";
import { AddToKnowledgeDialog } from "./add-to-knowledge-dialog";
import { ConversationAvatar } from "./conversation-avatar";
import { CustomerContextCard } from "./customer-context-card";
import { IdentityList, LeadBlock, MiniStats } from "./details-sections";
import type { useConversationActions } from "./inbox-api";
import type { ConversationDetail } from "./inbox-model";
import { RatingBlock } from "./rating-block";
import { SnoozeBlock } from "./snooze-block";
import { LinkedTickets } from "./ticket-chips";
import { TagEditor } from "./tag-editor";
import { knowledgeCandidate } from "./timeline";
import type { InboxPermissions } from "./use-inbox-permissions";

interface DetailsPanelProps {
  detail: ConversationDetail;
  actions: ReturnType<typeof useConversationActions>;
  permissions: InboxPermissions;
  now: number;
}

function Section({ title, empty, children }: { title: string; empty: string | null; children: ReactNode }) {
  return (
    <div>
      <div className="label">{title}</div>
      {empty ? <p className="text-xs text-fg-subtle">{empty}</p> : children}
    </div>
  );
}

function History({ detail }: { detail: ConversationDetail }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const fmt = useFormatters();
  return (
    <>
      <Section title={t("engage.inbox.details.sourcesUsed")} empty={detail.sources_used.length ? null : t("engage.inbox.details.noSources")}>
        <ul className="space-y-1.5 text-xs">
          {detail.sources_used.map((s) => (
            <li key={s.source_id}>
              <AppLink href={consoleRoutes.sourceDetail(summary.slug, s.source_id)} className="flex items-center gap-2 hover:text-primary-ink">
                <Icon name="file-text" className="size-3.5 text-fg-subtle" />
                <span className="truncate">{s.title ?? s.source_id}</span>
                <span className="ml-auto text-fg-subtle">×{s.count}</span>
              </AppLink>
            </li>
          ))}
        </ul>
      </Section>
      <Section title={t("engage.inbox.details.previous")} empty={detail.previous_conversations.length ? null : t("engage.inbox.details.noPrevious")}>
        <ul className="space-y-1.5 text-xs">
          {detail.previous_conversations.map((p) => (
            <li key={p.id}>
              <AppLink href={consoleRoutes.inbox(summary.slug, p.id)} className="flex items-center justify-between gap-2 rounded-md border border-line px-2.5 py-2 hover:border-primary">
                <span className="truncate">{p.title ?? t("engage.inbox.details.untitled")}</span>
                <span className="shrink-0 text-fg-subtle">
                  {fmt.date(p.last_message_at)} · {p.handled_by === "staff" ? t("engage.inbox.details.handledByStaff") : t("engage.inbox.details.handledByAi")}
                </span>
              </AppLink>
            </li>
          ))}
        </ul>
      </Section>
      <Section title={t("engage.inbox.details.actionsTaken")} empty={detail.actions_taken.length ? null : t("engage.inbox.details.noActions")}>
        <ol className="relative space-y-2.5 border-l border-line pl-4 text-xs">
          {detail.actions_taken.map((a) => (
            <li key={a.id}>
              <span className={cn("absolute -left-1 mt-1 size-2 rounded-pill", a.status === "success" ? "bg-primary" : "bg-danger-fill")} aria-hidden="true" />
              <span className="font-semibold">{a.name}</span>{" "}
              <span className="text-fg-subtle">
                · {a.http_status !== null ? t("engage.inbox.details.actionStatus", { status: a.http_status, ms: a.duration_ms ?? 0 }) : a.status} · {fmt.time(a.at)}
              </span>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}

/** Right pane: visitor, verified customer (M29), lead, tags, stats + SLA, sources, history, actions, knowledge/export. */
export function DetailsPanel({ detail, actions, permissions, now }: DetailsPanelProps) {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const { conversation, visitor } = detail;
  const candidate = knowledgeCandidate(detail.messages);

  const exportTranscript = () => {
    actions.exportTranscript.mutateAsync().then(
      (blob) => {
        const file = datedFilename(conversation.visitor_name.replace(/[^\w-]+/g, "-"), "txt");
        saveBlob(blob, file);
        toast.success(t("engage.inbox.details.exported", { file }));
      },
      (error: unknown) => toast.error(errorMessage(error)),
    );
  };

  return (
    <div className="space-y-5 p-4">
      <div className="flex items-center gap-3">
        <ConversationAvatar name={conversation.visitor_name} size="lg" />
        <div className="min-w-0">
          <div className="truncate font-bold">{visitor.display_name}</div>
          {visitor.verified ? (
            <div className="flex items-center gap-1 text-xs font-semibold text-success">
              <Icon name="shield-check" className="size-3" />
              {[visitor.role_label, t("engage.inbox.details.verified")].filter(Boolean).join(" · ")}
            </div>
          ) : (
            <div className="text-xs text-fg-subtle">{[visitor.role_label, t("engage.inbox.details.notVerified")].filter(Boolean).join(" · ")}</div>
          )}
        </div>
      </div>
      {detail.customer ? <CustomerContextCard conversationId={conversation.id} endUserId={detail.customer.end_user_id} /> : null}
      <IdentityList detail={detail} />
      <LeadBlock detail={detail} />
      <TagEditor tags={conversation.tags} editable={permissions.reply} onChange={(tags) => actions.patch.mutateAsync({ tags, clear_assignee: false })} />
      <SnoozeBlock detail={detail} actions={actions} canReply={permissions.reply} now={now} />
      <ConversationAttributesBlock conversationId={conversation.id} canEdit={permissions.reply} />
      <MiniStats detail={detail} now={now} />
      <RatingBlock conversation={conversation} />
      <LinkedTickets conversationId={conversation.id} />
      <History detail={detail} />
      <div className="grid grid-cols-1 gap-2">
        {permissions.addToKnowledge ? (
          <Dialog open={knowledgeOpen} onOpenChange={setKnowledgeOpen}>
            <Button variant="secondary" disabled={!candidate} onClick={() => { setKnowledgeOpen(true); }}>
              <Icon name="database" />
              {t("engage.inbox.details.addToKnowledge")}
            </Button>
            {candidate && knowledgeOpen ? (
              <AddToKnowledgeDialog candidate={candidate} agentId={conversation.agent_id} onSubmit={(body) => actions.addToKnowledge.mutateAsync(body)} onDone={() => { setKnowledgeOpen(false); }} />
            ) : null}
          </Dialog>
        ) : null}
        {permissions.export ? (
          <Button variant="ghost" loading={actions.exportTranscript.isPending} onClick={exportTranscript}>
            <Icon name="download" />
            {t("engage.inbox.details.exportTranscript")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
